import React, { useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import {
  LayoutDashboard,
  User,
  BookOpen,
  Image,
  FileText,
  LogOut,
  Menu,
  ChevronLeft,
  ChevronRight,
  Home,
  BookMarked,
} from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import clsx from 'clsx'
import { shouldShowBookmyyogattcVerificationDocumentsBanner } from '../utils/bookmyyogattcVerificationReminder'
import PlatformBusinessRegistration from './PlatformBusinessRegistration'

interface SchoolLayoutProps {
  children: React.ReactNode
}

const SchoolLayout: React.FC<SchoolLayoutProps> = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [desktopSidebarOpen, setDesktopSidebarOpen] = useState(true)
  const { user, selectedSchool, logout } = useAuth()
  const location = useLocation()
  const navigate = useNavigate()

  const showVerificationDocsBanner = shouldShowBookmyyogattcVerificationDocumentsBanner(selectedSchool)
  const onDocumentsPage = location.pathname.startsWith('/profile/school-documents')

  const navigation = [
    { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
    { name: 'Profile', href: '/profile', icon: User },
    { name: 'Courses', href: '/courses', icon: BookOpen },
    { name: 'Bookings', href: '/bookings', icon: FileText },
    { name: 'Accommodation', href: '/rooms', icon: Home },
    { name: 'Media', href: '/media', icon: Image },
    { name: 'Documentation', href: '/documentation', icon: BookMarked },
  ]

  const handleLogout = () => {
    logout()
    navigate('/login')
  }

  const isActive = (href: string) => {
    if (href === '/documentation') {
      return location.pathname === href || location.pathname.startsWith('/documentation/')
    }
    return location.pathname === href
  }

  return (
    <div className="min-h-screen bg-gray-50">
          {/* Mobile sidebar - full height drawer */}
          <div className={clsx(
            'fixed inset-0 z-50 lg:hidden',
            sidebarOpen ? 'block' : 'hidden'
          )}>
            <div className="fixed inset-0 bg-black/70 backdrop-blur-md" onClick={() => setSidebarOpen(false)} />
            <div className="fixed left-0 top-0 bottom-0 h-full w-full max-w-xs flex flex-col bg-gradient-to-br from-white/95 via-slate-50/90 to-gray-100/85 backdrop-blur-3xl shadow-2xl border-r border-white/20 overflow-hidden" style={{boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(255, 255, 255, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.4)'}}>
              
              {/* Header with premium design */}
              <div className="relative px-4 py-3 border-b border-white/20">
                <div className="absolute inset-0 bg-gradient-to-r from-primary-500/10 via-primary-600/10 to-primary-700/10"></div>
                <div className="relative flex items-center justify-between">
                  <div className="flex items-center space-x-3 min-w-0 flex-1">
                    <div className="flex-shrink-0">
                      <img 
                        src="/logo_icon.png" 
                        alt="Logo" 
                        className="h-10 w-10 object-contain"
                      />
                    </div>
                    <div className="min-w-0 flex-1">
                      <h1 className="text-sm font-bold bg-gradient-to-r from-gray-900 via-gray-800 to-gray-700 bg-clip-text text-transparent truncate">
                        {selectedSchool?.name || 'Yoga School'}
                      </h1>
                      <p className="text-xs text-gray-600 font-medium">Admin</p>
                    </div>
                  </div>
                  <button
                    onClick={() => setSidebarOpen(false)}
                    className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-white/50 rounded-lg transition-all duration-300 hover:scale-110 backdrop-blur-sm flex-shrink-0 ml-2"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </button>
                </div>
              </div>
              
              {/* Navigation with premium design - flex-1 min-h-0 so it fills height and can scroll */}
              <nav className="flex-1 min-h-0 overflow-y-auto px-3 py-4">
                <ul className="space-y-1">
                  {navigation.map((item) => {
                    const Icon = item.icon
                    return (
                      <li key={item.name}>
                        <Link
                          to={item.href}
                          onClick={() => setSidebarOpen(false)}
                          className={clsx(
                            'group flex items-center px-3 py-2 text-sm font-medium rounded-xl transition-all duration-300 relative overflow-hidden',
                            isActive(item.href)
                              ? 'bg-gradient-to-r from-primary-500 via-primary-600 to-primary-700 text-white shadow-xl transform scale-105 ring-2 ring-primary-200/50'
                              : 'text-gray-700 hover:text-gray-900 hover:bg-gradient-to-r hover:from-gray-50 hover:to-white hover:shadow-lg hover:scale-105'
                          )}
                        >
                          <div className={clsx(
                            'p-1.5 rounded-lg mr-3 transition-all duration-300',
                            isActive(item.href) 
                              ? 'bg-white/20 shadow-lg' 
                              : 'bg-gradient-to-br from-gray-100 to-gray-200 group-hover:from-primary-100 group-hover:to-primary-200 shadow-md'
                          )}>
                            <Icon className="h-4 w-4" />
                          </div>
                          <span className="font-semibold">{item.name}</span>
                          {isActive(item.href) && (
                            <motion.div
                              className="absolute right-2 top-1/2 transform -translate-y-1/2 w-1.5 h-1.5 bg-white rounded-full shadow-lg"
                              layoutId="mobile-sidebar-indicator"
                            />
                          )}
                        </Link>
                      </li>
                    )
                  })}
                </ul>
              </nav>

              {/* User section with premium design */}
              <div className="border-t border-white/20 p-3 bg-gradient-to-r from-gray-50/80 via-white/60 to-gray-50/80 backdrop-blur-sm">
                <div className="flex items-center mb-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-emerald-400 via-cyan-400 to-blue-400 rounded-xl flex items-center justify-center shadow-xl ring-2 ring-white/30">
                    <User className="h-5 w-5 text-white" />
                  </div>
                  <div className="ml-3 min-w-0">
                    <p className="text-sm font-bold text-gray-900 truncate">
                      {user?.firstName} {user?.lastName}
                    </p>
                    <p className="text-xs text-gray-600 truncate font-medium">{user?.email}</p>
                  </div>
                </div>

                <PlatformBusinessRegistration variant="sidebar" className="mb-3 pt-3 border-t border-white/30" />

                <button
                  onClick={handleLogout}
                  className="w-full flex items-center px-3 py-2 text-sm font-semibold text-gray-700 hover:text-white hover:bg-gradient-to-r hover:from-red-500 hover:to-pink-500 rounded-xl transition-all duration-300 group backdrop-blur-sm hover:shadow-lg hover:scale-105"
                >
                  <div className="p-1 rounded-lg bg-red-100 group-hover:bg-white/20 mr-3 transition-all duration-300 shadow-md">
                    <LogOut className="h-4 w-4 group-hover:scale-110 transition-transform" />
                  </div>
                  <span className="truncate">Sign out</span>
                </button>
              </div>
            </div>
          </div>

          {/* Desktop sidebar */}
          <div className={`hidden lg:fixed lg:inset-y-0 lg:flex lg:flex-col transition-all duration-300 ease-in-out ${desktopSidebarOpen ? 'lg:w-72' : 'lg:w-0'}`}>
            <div className={`flex flex-col flex-grow relative overflow-hidden transition-all duration-300 ease-in-out ${desktopSidebarOpen ? 'w-72 opacity-100' : 'w-0 opacity-0'} rounded-3xl`} style={{boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(255, 255, 255, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.4)'}}>
              {/* Stunning glassmorphism background */}
              <div className="absolute inset-0 bg-gradient-to-br from-white/95 via-slate-50/90 to-gray-100/85 backdrop-blur-3xl rounded-3xl"></div>
              <div className="absolute inset-0 bg-gradient-to-r from-primary-500/5 via-primary-600/5 to-primary-700/5 rounded-3xl"></div>
              <div className="absolute inset-0 border border-white/20 rounded-3xl"></div>
              
              {/* Content */}
              <div className="relative z-10 flex flex-col h-full">
                {/* Header with premium design */}
                <div className="relative px-4 py-3 border-b border-white/20">
                  <div className="absolute inset-0 bg-gradient-to-r from-primary-500/10 via-primary-600/10 to-primary-700/10 rounded-t-3xl"></div>
                  <div className="relative flex items-center justify-between">
                    <div className={`flex items-center space-x-3 min-w-0 flex-1 transition-all duration-500 ${desktopSidebarOpen ? 'opacity-100' : 'opacity-0'}`}>
                      <div className="flex-shrink-0">
                        <img 
                          src="/logo_icon.png" 
                          alt="Logo" 
                          className="h-10 w-10 object-contain"
                        />
                      </div>
                      <div className="min-w-0 flex-1">
                        <h1 className="text-sm font-bold bg-gradient-to-r from-gray-900 via-gray-800 to-gray-700 bg-clip-text text-transparent truncate">
                          {selectedSchool?.name || 'Yoga School'}
                        </h1>
                        <p className="text-xs text-gray-600 font-medium">Admin</p>
                      </div>
                    </div>
                    <button
                      onClick={() => setDesktopSidebarOpen(false)}
                      className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-white/50 rounded-lg transition-all duration-300 hover:scale-110 backdrop-blur-sm flex-shrink-0 ml-2"
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </button>
                  </div>
                </div>
                
                {/* Navigation with premium design */}
                <nav className={`flex-1 px-3 py-4 transition-all duration-500 ${desktopSidebarOpen ? 'opacity-100' : 'opacity-0'}`}>
                  <ul className="space-y-1">
                    {navigation.map((item) => {
                      const Icon = item.icon
                      return (
                        <li key={item.name}>
                          <Link
                            to={item.href}
                            className={clsx(
                              'group flex items-center px-3 py-2 text-sm font-medium rounded-xl transition-all duration-300 relative overflow-hidden',
                              isActive(item.href)
                                ? 'bg-gradient-to-r from-primary-500 via-primary-600 to-primary-700 text-white shadow-xl transform scale-105 ring-2 ring-primary-200/50'
                                : 'text-gray-700 hover:text-gray-900 hover:bg-gradient-to-r hover:from-gray-50 hover:to-white hover:shadow-lg hover:scale-105'
                            )}
                          >
                            <div className={clsx(
                              'p-1.5 rounded-lg mr-3 transition-all duration-300',
                              isActive(item.href) 
                                ? 'bg-white/20 shadow-lg' 
                                : 'bg-gradient-to-br from-gray-100 to-gray-200 group-hover:from-primary-100 group-hover:to-primary-200 shadow-md'
                            )}>
                              <Icon className="h-4 w-4" />
                            </div>
                            <span className="font-semibold">{item.name}</span>
                            {isActive(item.href) && (
                              <motion.div
                                className="absolute right-2 top-1/2 transform -translate-y-1/2 w-1.5 h-1.5 bg-white rounded-full shadow-lg"
                                layoutId="sidebar-indicator"
                              />
                            )}
                          </Link>
                        </li>
                      )
                    })}
                  </ul>
                </nav>

                {/* User section with premium design */}
                <div className={`border-t border-white/20 p-3 bg-gradient-to-r from-gray-50/80 via-white/60 to-gray-50/80 backdrop-blur-sm rounded-b-3xl transition-all duration-500 ${desktopSidebarOpen ? 'opacity-100' : 'opacity-0'}`}>
                  <div className="flex items-center mb-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-emerald-400 via-cyan-400 to-blue-400 rounded-xl flex items-center justify-center shadow-xl ring-2 ring-white/30">
                      <User className="h-5 w-5 text-white" />
                    </div>
                    <div className="ml-3 min-w-0">
                      <p className="text-sm font-bold text-gray-900 truncate">
                        {user?.firstName} {user?.lastName}
                      </p>
                      <p className="text-xs text-gray-600 truncate font-medium">{user?.email}</p>
                    </div>
                  </div>

                  <PlatformBusinessRegistration variant="sidebar" className="mb-3 pt-3 border-t border-white/30" />

                  <button
                    onClick={handleLogout}
                    className="w-full flex items-center px-3 py-2 text-sm font-semibold text-gray-700 hover:text-white hover:bg-gradient-to-r hover:from-red-500 hover:to-pink-500 rounded-xl transition-all duration-300 group backdrop-blur-sm hover:shadow-lg hover:scale-105"
                  >
                    <div className="p-1 rounded-lg bg-red-100 group-hover:bg-white/20 mr-3 transition-all duration-300 shadow-md">
                      <LogOut className="h-4 w-4 group-hover:scale-110 transition-transform" />
                    </div>
                    <span className="truncate">Sign out</span>
                  </button>
                </div>
              </div>
            </div>
          </div>

      {/* Main content */}
      <div className={`transition-all duration-300 ease-in-out ${desktopSidebarOpen ? 'lg:pl-72' : 'lg:pl-0'}`}>
        {/* Page content */}
        <main className="relative min-h-screen">
          {showVerificationDocsBanner && (
            <div className="sticky top-0 z-40 px-4 sm:px-8 pt-4">
              <div className="max-w-4xl mx-auto rounded-xl border border-amber-200 bg-amber-50 shadow-sm px-4 py-3 text-sm text-amber-950">
                <p className="font-semibold text-amber-900">Verification documents required</p>
                <p className="mt-1 text-amber-900/90 leading-relaxed">
                Kindly upload your business registration and address proof documents, along with any optional supporting materials, to complete the verification of your school.
                </p>
                {!onDocumentsPage ? (
                  <Link
                    to="/profile/school-documents"
                    className="mt-2 inline-flex items-center gap-1.5 font-semibold text-primary-700 hover:text-primary-800"
                  >
                    <FileText className="h-4 w-4" />
                    Go to document verification page
                  </Link>
                ) : <></>}
              </div>
            </div>
          )}
          {/* Floating toggle: mobile = menu button; desktop = chevron when sidebar closed */}
          <div className="fixed top-4 left-4 z-50 flex flex-col gap-3">
            <button
              type="button"
              className="lg:hidden flex items-center justify-center w-12 h-12 text-white bg-gradient-to-r from-primary-500 via-primary-600 to-primary-700 hover:from-primary-600 hover:via-primary-700 hover:to-primary-800 transition-all duration-300 rounded-2xl shadow-2xl hover:shadow-3xl hover:scale-110 backdrop-blur-sm ring-4 ring-white/20 touch-manipulation"
              onClick={() => setSidebarOpen(true)}
              title="Open Menu"
              aria-label="Open menu"
            >
              <Menu className="h-6 w-6" />
            </button>
            {!desktopSidebarOpen && (
              <button
                type="button"
                className="hidden lg:flex items-center justify-center w-12 h-12 text-white bg-gradient-to-r from-primary-500 via-primary-600 to-primary-700 hover:from-primary-600 hover:via-primary-700 hover:to-primary-800 transition-all duration-300 rounded-2xl shadow-2xl hover:shadow-3xl hover:scale-110 backdrop-blur-sm ring-4 ring-white/20"
                onClick={() => setDesktopSidebarOpen(true)}
                title="Show Sidebar"
              >
                <ChevronRight className="h-6 w-6" />
              </button>
            )}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="p-8 pt-20"
          >
            {children}
          </motion.div>
        </main>
      </div>
    </div>
  )
}

export default SchoolLayout
